'use client';

import {
    carrierInstructions,
    carriers,
    type Carrier,
    type ForwardingType,
    type SpecialArticleId,
} from '@/data/helpCenterContent';
import { RadioGroup } from '@headlessui/react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';

interface HelpArticleContentProps {
  content: React.ReactNode | SpecialArticleId;
}

function SMSSummariesSetup() {
  return (
    <div className="space-y-6">
      <p className="text-gray-700 leading-relaxed">
        Receive instant text message summaries of every call, including key information and next steps.
      </p>
      
      {/* Image showing Edit AI Agent modal */}
      <div>
        <img 
          src="/images/help/edit-ai-agent-sms.png" 
          alt="Edit AI Agent modal showing SMS Notification Number field"
          className="w-full max-w-2xl rounded-lg border border-gray-200 shadow-sm"
        />
      </div>

      {/* How to Enable SMS Summaries */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="font-semibold text-blue-900 mb-3 text-lg">How to Enable SMS Summaries</h3>
        <ol className="list-decimal list-inside space-y-2 text-blue-800">
          <li className="pl-2">Navigate to your dashboard</li>
          <li className="pl-2">Click to edit your AI agent</li>
          <li className="pl-2">In the "SMS Notification Number" field, enter your phone number including area code and country code (e.g., +1 415 877-3166)</li>
          <li className="pl-2">Check the box to agree to receive text messages at this number</li>
          <li className="pl-2">Save your changes</li>
        </ol>
      </div>

      {/* How to Opt Out */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
        <h3 className="font-semibold text-gray-900 mb-3 text-lg">How to Opt Out</h3>
        <p className="text-gray-700 mb-3">
          You can opt out of SMS notifications at any time using either method:
        </p>
        <ul className="list-disc list-inside space-y-2 text-gray-700">
          <li className="pl-2">
            <strong>Via Web UI:</strong> Navigate to your dashboard, edit your AI agent, and remove or clear the SMS Notification Number field, then save your changes.
          </li>
          <li className="pl-2">
            <strong>Via SMS:</strong> Reply to any SMS notification message with "STOP" to opt out immediately.
          </li>
        </ul>
      </div>
    </div>
  );
}

function InteractiveForwardingTool() {
  const [forwardingType, setForwardingType] = useState<ForwardingType>('immediate');
  const [selectedCarrier, setSelectedCarrier] = useState<Carrier | ''>('');

  return (
    <div className="space-y-8">
      {/* How It Works Section */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <div className="flex items-start gap-3">
          <InformationCircleIcon className="h-6 w-6 text-blue-600 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="font-semibold text-blue-900 mb-2 text-lg">How It Works</h3>
            <p className="text-blue-800 leading-relaxed">
              You can use Greet with your existing phone number by setting up call forwarding. 
              This way, you keep your current number public while Greet handles all incoming calls 
              behind the scenes. When someone calls your existing number, the call will be 
              automatically forwarded to your Greet number where our AI will answer and handle 
              the conversation.
            </p>
          </div>
        </div>
      </div>

      {/* Options Overview */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-gray-50 rounded-lg p-6">
          <h3 className="font-semibold text-gray-900 mb-2 text-lg">Option 1: Forward to Greet</h3>
          <p className="text-gray-700">
            Keep your existing number public and forward all calls to your Greet number. 
            Your customers call your familiar number, but Greet handles the conversation.
          </p>
        </div>
        <div className="bg-gray-50 rounded-lg p-6">
          <h3 className="font-semibold text-gray-900 mb-2 text-lg">Option 2: Use Greet as Main</h3>
          <p className="text-gray-700">
            Use your Greet number as your main business number and share it publicly. 
            Your existing number becomes a backup or personal number.
          </p>
        </div>
      </div>

      {/* Forwarding Type Selection */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Choose Your Forwarding Preference
        </h3>
        <RadioGroup value={forwardingType} onChange={setForwardingType}>
          <div className="grid md:grid-cols-2 gap-4">
            <RadioGroup.Option value="immediate" className={({ checked }) => 
              `relative w-full text-left border rounded-lg p-4 cursor-pointer transition-all ${
                checked 
                  ? 'border-blue-600 bg-blue-50' 
                  : 'border-gray-300 hover:border-blue-400'
              }`
            }>
              {({ checked }) => (
                <>
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                      checked ? 'border-blue-600 bg-blue-600' : 'border-gray-300'
                    }`}>
                      {checked && <div className="w-2 h-2 bg-white rounded-full"></div>}
                    </div>
                    <span className="font-semibold text-gray-900">Immediate Forwarding</span>
                  </div>
                  <p className="text-gray-600 text-sm">
                    All calls go directly to Greet without ringing your phone first.
                  </p>
                </>
              )}
            </RadioGroup.Option>
            
            <RadioGroup.Option value="no-answer" className={({ checked }) => 
              `relative w-full text-left border rounded-lg p-4 cursor-pointer transition-all ${
                checked 
                  ? 'border-blue-600 bg-blue-50' 
                  : 'border-gray-300 hover:border-blue-400'
              }`
            }>
              {({ checked }) => (
                <>
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                      checked ? 'border-blue-600 bg-blue-600' : 'border-gray-300'
                    }`}>
                      {checked && <div className="w-2 h-2 bg-white rounded-full"></div>}
                    </div>
                    <span className="font-semibold text-gray-900">Forward When No Answer</span>
                  </div>
                  <p className="text-gray-600 text-sm">
                    Calls ring your phone first, then forward to Greet if you don't answer.
                  </p>
                </>
              )}
            </RadioGroup.Option>
          </div>
        </RadioGroup>
      </div>

      {/* Carrier Selection */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Select Your Carrier
        </h3>
        <select
          value={selectedCarrier}
          onChange={(e) => setSelectedCarrier(e.target.value as Carrier)}
          className="w-full md:w-64 border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Choose your carrier...</option>
          {carriers.map((carrier) => (
            <option key={carrier} value={carrier}>{carrier}</option>
          ))}
        </select>
      </div>

      {/* Dynamic Instructions */}
      {selectedCarrier && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Instructions for {selectedCarrier}
          </h3>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
            <span className="font-semibold text-gray-900 mb-3 block">
              {forwardingType === 'immediate' ? 'Immediate Call Forwarding' : 'Call Forwarding When No Answer'}
            </span>
            <ol className="list-decimal list-inside space-y-2 text-gray-700">
              {carrierInstructions[selectedCarrier][forwardingType].map((step, stepIndex) => (
                <li key={stepIndex} className="pl-2">{step}</li>
              ))}
            </ol>
          </div>
        </div>
      )}

      {/* Additional Notes */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
        <h3 className="font-semibold text-yellow-900 mb-2 text-lg">Important Notes:</h3>
        <ul className="text-yellow-800 space-y-1">
          <li>• Some carriers require you to contact customer service to set up forwarding</li>
          <li>• Make sure to test the forwarding after setup</li>
          <li>• Keep your Greet number active to receive forwarded calls</li>
          {forwardingType === 'no-answer' && (
            <li>• You can still answer calls on your phone before they forward to Greet</li>
          )}
        </ul>
      </div>

      {/* SEO-friendly static content for search engines */}
      <div className="mt-8 border-t pt-6 prose prose-sm max-w-none">
        <h2>Complete Call Forwarding Guide</h2>
        <p>
          This section provides comprehensive instructions for setting up call forwarding 
          with all major carriers to use your existing phone number with Greet.
        </p>

        <h3>Forwarding Types Explained</h3>
        <p><strong>Immediate Forwarding:</strong> All calls go directly to Greet without ringing your phone first.</p>
        <p><strong>Forward When No Answer:</strong> Calls ring your phone first, then forward to Greet if you don't answer.</p>

        <h3>Carrier-Specific Instructions</h3>
        <p><strong>Verizon:</strong> For immediate forwarding call *72, for no-answer forwarding call *90. Enter your Greet number and press #. To disable call *73 (immediate) or *91 (no-answer).</p>
        <p><strong>AT&T:</strong> For immediate forwarding call *21*, for no-answer forwarding call *61*. Enter your Greet number and press #. To disable call #21# (immediate) or #61# (no-answer).</p>
        <p><strong>T-Mobile, Cricket, Metro & Other Carriers:</strong> Most carriers require calling customer service at 611 to set up call forwarding. Ask to set up either immediate call forwarding or call forwarding when no answer, and provide your Greet phone number.</p>

        <h3>Setup Tips and Troubleshooting</h3>
        <ul>
          <li>Some carriers require you to contact customer service to set up forwarding</li>
          <li>Make sure to test the forwarding after setup</li>
          <li>Keep your Greet number active to receive forwarded calls</li>
          <li>With no-answer forwarding, you can still answer calls on your phone before they forward to Greet</li>
        </ul>
      </div>
    </div>
  );
}

export default function HelpArticleContent({ content }: HelpArticleContentProps) {
  // Handle special article types
  if (content === 'sms-summaries-setup') {
    return <SMSSummariesSetup />;
  }
  
  if (content === 'interactive-forwarding-tool') {
    return <InteractiveForwardingTool />;
  }
  
  // Handle JSX content - wrap in styled container
  return (
    <div className="prose prose-gray max-w-none [&>div>*:first-child]:mt-0">
      <div className="space-y-4 text-gray-700 leading-relaxed">
        {content}
      </div>
    </div>
  );
}

